"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  /** Enable month/year dropdown navigation - useful for DOB selection */
  captionLayout?: "label" | "dropdown" | "dropdown-months" | "dropdown-years"
  /** Start year for dropdown (default: 1950) */
  startYear?: number
  /** End year for dropdown (default: current year) */
  endYear?: number
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  startYear,
  endYear,
  ...props
}: CalendarProps) {
  // For dropdown mode, provide reasonable year range defaults
  const currentYear = new Date().getFullYear()
  const fromYear = startYear ?? (captionLayout?.includes("dropdown") ? 1950 : currentYear - 10)
  const toYear = endYear ?? currentYear

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      startMonth={new Date(fromYear, 0)}
      endMonth={new Date(toYear, 11)}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        month_caption: "flex justify-center pt-1 relative items-center gap-2",
        caption_label: captionLayout === "label" ? "text-sm font-medium" : "hidden",
        dropdowns: "flex gap-2 items-center",
        dropdown: cn(
          "appearance-none bg-background border border-input rounded-md px-2 py-1 text-sm font-medium",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
          "cursor-pointer hover:bg-accent"
        ),
        nav: "space-x-1 flex items-center",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1"
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
        ),
        range_end: "day-range-end",
        selected:
          "bg-primary-action text-primary-foreground hover:bg-primary-action hover:text-primary-foreground focus:bg-primary-action focus:text-primary-foreground",
        today: "bg-accent text-accent-foreground",
        outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight
          return <Icon className="h-4 w-4" />
        },
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
